import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Textarea,
  NumberInput,
  NumberInputField,
  CheckboxGroup,
  Checkbox,
  Stack,
  useDisclosure,
  useToast,
  VStack,
  HStack,
  Text,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner,
  Center,
  Card,
  CardBody,
  Grid,
  GridItem,
  Divider,
  Spacer,
  List,
  ListItem,
} from "@chakra-ui/react";
import { SearchIcon, EditIcon, DeleteIcon, AddIcon, CalendarIcon, ChevronDownIcon, ChevronRightIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { useFindMany, useAction, useUser, useFetch } from "@gadgetinc/react";
import { api } from "../api";

const FACILITY_TYPES = [
  { value: "hospital_system", label: "Hospital System" },
  { value: "fire_department", label: "Fire Department" },
  { value: "ems_service", label: "EMS Service" },
  { value: "private_ambulance", label: "Private Ambulance Service" },
  { value: "urgent_care", label: "Urgent Care Center" },
  { value: "clinic", label: "Clinic/Medical Practice" },
  { value: "long_term_care", label: "Long Term Care Facility" },
  { value: "rehabilitation_center", label: "Rehabilitation Center" },
  { value: "educational_institution", label: "Educational Institution" },
  { value: "government_agency", label: "Government Agency" },
  { value: "other", label: "Other" },
];

const SHIFT_TYPES = [
  { value: "field", label: "Field" },
  { value: "hospital", label: "Hospital" },
  { value: "lab", label: "Lab" },
  { value: "field_internship", label: "Field Internship" },
];

const COHORT_OPTIONS = [
  { value: "EMT_Basic_2024", label: "EMT Basic 2024" },
  { value: "EMT_Basic_2025", label: "EMT Basic 2025" },
  { value: "EMT_Advanced_2024", label: "EMT Advanced 2024" },
  { value: "EMT_Advanced_2025", label: "EMT Advanced 2025" },
  { value: "Paramedic_2024", label: "Paramedic 2024" },
  { value: "Paramedic_2025", label: "Paramedic 2025" },
  { value: "Nursing_2024", label: "Nursing 2024" },
  { value: "Nursing_2025", label: "Nursing 2025" },
  { value: "Field_Internship_2024", label: "Field Internship 2024" },
  { value: "Field_Internship_2025", label: "Field Internship 2025" },
];

const GEOFENCE_TYPES = [
  { value: "none", label: "No Geofencing" },
  { value: "circular", label: "Circular Radius" },
  { value: "polygon", label: "Custom Polygon" },
];

// AddressSearch Component
function AddressSearch({ onAddressSelect, placeholder = "Enter address to search..." }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [isAddressSelected, setIsAddressSelected] = useState(false);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [debounceTimeout]);

  // Debounced autocomplete search
  useEffect(() => {
    // Don't search if an address has been selected (prevents dropdown from reappearing)
    if (isAddressSelected) {
      return;
    }

    // Clear existing timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Clear results if query is empty
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSelectedIndex(-1);
      return;
    }

    // Set new timeout for debounced search
    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch('/maps/geocode', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query: searchQuery.trim() })
        });

        if (!response.ok) {
          throw new Error('Failed to search addresses');
        }

        const data = await response.json();
        console.log('Autocomplete API response:', data);
        // Handle both possible response formats from backend
        const results = data.results || data.predictions || [];
        setSearchResults(results.slice(0, 5));
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Address autocomplete error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce delay

    setDebounceTimeout(timeoutId);
  }, [searchQuery, isAddressSelected]);

  const selectAddress = async (result) => {
    setIsSearching(true);
    // Immediately clear results and prevent further searches
    setSearchResults([]);
    setSelectedIndex(-1);
    setIsAddressSelected(true);
    
    try {
      // Always use placeId if available to get full details
      if (result.placeId) {
        const response = await fetch('/maps/geocode', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ placeId: result.placeId })
        });

        if (!response.ok) {
          throw new Error('Failed to get place details');
        }

        const data = await response.json();
        console.log('Place details API response:', data);
        
        if (data.results && data.results[0] && data.results[0].latitude && data.results[0].longitude) {
          const addressData = {
            formatted_address: data.results[0].address,
            latitude: data.results[0].latitude,
            longitude: data.results[0].longitude
          };
          onAddressSelect(addressData);
          setSearchQuery(data.results[0].address);
        } else {
          throw new Error('Invalid place details response');
        }
      } else if (result.geometry && result.geometry.location) {
        // Fallback for results that already have coordinates
        const addressData = {
          formatted_address: result.formatted_address || result.description,
          latitude: result.geometry.location.lat,
          longitude: result.geometry.location.lng
        };
        onAddressSelect(addressData);
        setSearchQuery(result.formatted_address || result.description);
      } else {
        throw new Error('No coordinates available for selected address');
      }
    } catch (error) {
      console.error('Address selection error:', error);
      // Reset state on error so user can try again
      setIsAddressSelected(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          selectAddress(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setSearchResults([]);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    setSelectedIndex(-1);
    // Reset address selected state when user starts typing new search
    if (isAddressSelected && e.target.value !== searchQuery) {
      setIsAddressSelected(false);
    }
  };

  return (
    <Box position="relative">
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          {isSearching ? (
            <Spinner size="sm" color="blue.400" />
          ) : (
            <SearchIcon color="gray.400" />
          )}
        </InputLeftElement>
        <Input
          placeholder={placeholder}
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          bg="gray.700"
          borderColor="gray.600"
          color="white"
          _placeholder={{ color: "gray.400" }}
          _focus={{ borderColor: "blue.400" }}
          autoComplete="off"
        />
      </InputGroup>

      {searchResults.length > 0 && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="gray.700"
          border="1px solid"
          borderColor="gray.600"
          borderRadius="md"
          mt={1}
          zIndex={1000}
          maxH="200px"
          overflowY="auto"
          boxShadow="lg"
        >
          <List spacing={0}>
            {searchResults.map((result, index) => (
              <ListItem
                key={result.placeId || index}
                p={3}
                cursor="pointer"
                bg={index === selectedIndex ? "blue.600" : "transparent"}
                _hover={{ bg: index === selectedIndex ? "blue.500" : "gray.600" }}
                borderBottom={index < searchResults.length - 1 ? "1px solid" : "none"}
                borderColor="gray.600"
                onClick={() => selectAddress(result)}
              >
                <Text fontSize="sm" color="white">
                  {result.formatted_address || result.description}
                </Text>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {isSearching && searchResults.length === 0 && searchQuery.trim() && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="gray.700"
          border="1px solid"
          borderColor="gray.600"
          borderRadius="md"
          mt={1}
          zIndex={1000}
          p={3}
        >
          <Text fontSize="sm" color="gray.400" textAlign="center">
            Searching for addresses...
          </Text>
        </Box>
      )}
    </Box>
  );
}

// MapPicker Component
const MAP_WIDTH = 500;
const MAP_HEIGHT = 300;

function MapPicker({
  latitude,
  longitude,
  address,
  onCoordinatesChange,
  geofenceType = "none",
  geofenceRadius = 1000,
  geofencePolygon = [],
  isCreatingPolygon = false,
  tempPolygonPoints = [],
  onGeofenceChange,
  onPolygonPointAdd,
  onPolygonComplete,
  onPolygonCancel,
  zoom = 17
}) {
  const [mapZoom, setMapZoom] = useState(zoom);
  const [mapData, setMapData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUpdatingCoordinates, setIsUpdatingCoordinates] = useState(false);
  const [showLocationUpdate, setShowLocationUpdate] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [mapRefreshKey, setMapRefreshKey] = useState(0);
  
  // Google Maps integration and loading state
  const [googleMap, setGoogleMap] = useState(null);
  const [googleMarker, setGoogleMarker] = useState(null);
  const [googleCircle, setGoogleCircle] = useState(null);
  const [googlePolygon, setGooglePolygon] = useState(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [googleMapsLoading, setGoogleMapsLoading] = useState(false);
  const [googleMapsError, setGoogleMapsError] = useState(null);
  const mapContainerRef = useRef(null);

  // Validate coordinates
  const hasValidCoordinates = 
    latitude !== null && 
    latitude !== undefined && 
    longitude !== null && 
    longitude !== undefined &&
    !isNaN(latitude) && 
    !isNaN(longitude) &&
    latitude >= -90 && 
    latitude <= 90 &&
    longitude >= -180 && 
    longitude <= 180;

  // Load Google Maps script dynamically
  const loadGoogleMapsScript = useCallback(async () => {
    setGoogleMapsLoading(true);
    setGoogleMapsError(null);
    
    try {
      // Check if already loaded
      if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
        setGoogleMapsLoading(false);
        return true;
      }

      // Load script from our backend endpoint
      const response = await fetch('/maps/js-api', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get Google Maps script URL: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.scriptUrl) {
        throw new Error('No script URL returned from backend');
      }

      // Create and load the script
      const script = document.createElement('script');
      script.src = data.scriptUrl;
      script.async = true;
      script.defer = true;

      return new Promise((resolve, reject) => {
        script.onload = () => {
          setGoogleMapsLoaded(true);
          setGoogleMapsLoading(false);
          resolve(true);
        };

        script.onerror = () => {
          const error = new Error('Failed to load Google Maps script');
          setGoogleMapsError(error);
          setGoogleMapsLoading(false);
          reject(error);
        };

        document.head.appendChild(script);
      });

    } catch (error) {
      setGoogleMapsError(error);
      setGoogleMapsLoading(false);
      throw error;
    }
  }, []);

  // Initialize Google Maps when entering edit mode
  useEffect(() => {
    if (isEditMode && hasValidCoordinates && mapContainerRef.current && !googleMap) {
      if (googleMapsLoaded && window.google && window.google.maps) {
        initializeGoogleMap();
      } else if (!googleMapsLoading && !googleMapsLoaded) {
        loadGoogleMapsScript().then(() => {
          if (mapContainerRef.current) {
            initializeGoogleMap();
          }
        }).catch(error => {
          // Failed to load Google Maps, falling back to static map
        });
      }
    }
  }, [isEditMode, hasValidCoordinates, googleMapsLoaded, googleMapsLoading, googleMap, loadGoogleMapsScript]);

  const initializeGoogleMap = useCallback(() => {
    if (!mapContainerRef.current) {
      return;
    }

    if (!window.google || !window.google.maps) {
      return;
    }

    try {
      const map = new window.google.maps.Map(mapContainerRef.current, {
        center: { lat: latitude, lng: longitude },
        zoom: mapZoom,
        mapTypeId: 'roadmap',
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.TOP_RIGHT
        },
        clickableIcons: false
      });

      setGoogleMap(map);
      
      // Initialize marker
      const marker = new window.google.maps.Marker({
        position: { lat: latitude, lng: longitude },
        map: map,
        draggable: true,
        title: 'Location Marker'
      });
      setGoogleMarker(marker);
      
      // Initialize circle
      const circle = new window.google.maps.Circle({
        center: { lat: latitude, lng: longitude },
        radius: geofenceRadius,
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.35,
        map: map
      });
      setGoogleCircle(circle);
      
      // Initialize polygon
      const polygon = new window.google.maps.Polygon({
        paths: geofencePolygon.map(point => ({ lat: point.lat, lng: point.lng })),
        strokeColor: '#FF0000',
        strokeOpacity: 1.0,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.4,
        editable: true,
        map: map
      });
      setGooglePolygon(polygon);

    } catch (error) {
      setGoogleMapsError(error);
    }
  }, [latitude, longitude, mapZoom, geofenceRadius, geofencePolygon]);

  // Fetch map data using regular fetch instead of useFetch
  useEffect(() => {
    if (!hasValidCoordinates) {
      setMapData(null);
      setMapError(null);
      return;
    }

    // Add a small delay to ensure coordinates are properly updated
    const timeoutId = setTimeout(() => {
      setMapLoading(true);
      setMapError(null);

      const fetchMapData = async () => {
        try {
          const response = await fetch('/maps/static-map', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              latitude: latitude,
              longitude: longitude,
              zoom: mapZoom,
              width: MAP_WIDTH,
              height: MAP_HEIGHT,
              mapType: 'hybrid',
              returnBase64: true
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          setMapData(data.signedUrl || data.imageUrl);
        } catch (error) {
          setMapError(error);
        } finally {
          setMapLoading(false);
        }
      };

      fetchMapData();
    }, 50); // Short delay to ensure coordinate state has propagated

    return () => clearTimeout(timeoutId);
  }, [hasValidCoordinates, latitude, longitude, mapZoom, mapRefreshKey]);

  // Calculate radius in pixels using Web Mercator projection
  const calculateRadiusPixels = (radiusMeters, centerLat, zoomLevel) => {
    // Web Mercator projection: meters per pixel at given latitude and zoom
    const metersPerPixel = (156543.03392 * Math.cos(centerLat * Math.PI / 180)) / Math.pow(2, zoomLevel);
    return radiusMeters / metersPerPixel;
  };

  // Snap radius to reasonable increments
  const snapRadius = (radius) => {
    if (radius <= 50) return Math.round(radius / 10) * 10;
    if (radius <= 200) return Math.round(radius / 25) * 25;
    if (radius <= 1000) return Math.round(radius / 50) * 50;
    if (radius <= 5000) return Math.round(radius / 100) * 100;
    return Math.round(radius / 500) * 500;
  };

  // Click to move pin handler
  const handleMapClick = async (event) => {
    if (!hasValidCoordinates || isUpdatingCoordinates) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Convert pixel coordinates to lat/lng
    const latLng = pixelToLatLng(x, y, latitude, longitude, mapZoom, MAP_WIDTH, MAP_HEIGHT);
    
    if (isCreatingPolygon && onPolygonPointAdd) {
      // Check if we should auto-close the polygon by clicking near the first point
      const allPoints = [...geofencePolygon, ...tempPolygonPoints];
      
      if (allPoints.length >= 3) {
        const firstPoint = allPoints[0];
        const firstPointPixel = latLngToPixel(firstPoint.lat, firstPoint.lng, latitude, longitude, mapZoom, MAP_WIDTH, MAP_HEIGHT);
        const clickPixel = { x, y };
        const distance = calculatePixelDistance(firstPointPixel, clickPixel);
        
        // Auto-close if within 15 pixels of the first point
        if (distance <= 15) {
          if (onPolygonComplete) {
            onPolygonComplete();
          }
          return;
        }
      }
      
      onPolygonPointAdd(latLng);
    } else if (isEditMode && !isCreatingPolygon) {
      await handleMovePin(latLng.lat, latLng.lng);
    }
  };

  // Handle moving pin to new location
  const handleMovePin = async (newLat, newLng) => {
    setIsUpdatingCoordinates(true);
    setShowLocationUpdate(true);
    
    try {
      // Update coordinates immediately for visual feedback
      if (onCoordinatesChange) {
        onCoordinatesChange(newLat, newLng);
      }
      
      // Wait a short moment for coordinate state to propagate before refreshing map
      setTimeout(() => {
        setMapRefreshKey(prev => prev + 1);
      }, 100);
      
      // Simulate API call to save coordinates
      // In a real implementation, this would call the backend
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Show success feedback briefly
      setTimeout(() => {
        setShowLocationUpdate(false);
      }, 800);
      
    } catch (error) {
      // Could show error toast here
    } finally {
      setIsUpdatingCoordinates(false);
    }
  };

  // Radius adjustment handler for click-to-adjust
  const handleRadiusAdjustClick = async (event) => {
    if (!isEditMode || geofenceType !== "circular") {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    
    const mapContainer = document.querySelector('[data-map-container]');
    if (!mapContainer) {
      return;
    }
    
    const rect = mapContainer.getBoundingClientRect();
    const centerX = rect.left + MAP_WIDTH / 2;
    const centerY = rect.top + MAP_HEIGHT / 2;
    
    const deltaX = event.clientX - centerX;
    const deltaY = event.clientY - centerY;
    const pixelRadius = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Convert pixel radius to meters
    const metersPerPixel = (156543.03392 * Math.cos(latitude * Math.PI / 180)) / Math.pow(2, mapZoom);
    const radiusMeters = pixelRadius * metersPerPixel;
    const snappedRadius = snapRadius(Math.max(10, Math.min(10000, radiusMeters)));
    
    console.log('🖱️ MapPicker: Radius click adjustment', {
      deltaX,
      deltaY,
      pixelRadius,
      metersPerPixel,
      radiusMeters,
      snappedRadius
    });
    
    if (onGeofenceChange) {
      onGeofenceChange('geofenceRadius', snappedRadius);
    }
  };

  const handleZoomIn = () => {
    console.log('🔍 MapPicker: Zoom in', { currentZoom: mapZoom, maxZoom: 20 });
    if (mapZoom < 20) {
      setMapZoom(prev => {
        const newZoom = prev + 1;
        console.log('🔍 MapPicker: Zoom updated', { from: prev, to: newZoom });
        // Delay map refresh to ensure zoom state has updated
        setTimeout(() => {
          setMapRefreshKey(current => current + 1);
        }, 50);
        return newZoom;
      });
    }
  };

  const handleZoomOut = () => {
    console.log('🔍 MapPicker: Zoom out', { currentZoom: mapZoom, minZoom: 1 });
    if (mapZoom > 1) {
      setMapZoom(prev => {
        const newZoom = prev - 1;
        console.log('🔍 MapPicker: Zoom updated', { from: prev, to: newZoom });
        // Delay map refresh to ensure zoom state has updated
        setTimeout(() => {
          setMapRefreshKey(current => current + 1);
        }, 50);
        return newZoom;
      });
    }
  };

  const handleRetry = () => {
    console.log('🔄 MapPicker: Retry requested', { hasValidCoordinates });
    if (hasValidCoordinates) {
      // Re-fetch static map by forcing refresh
      console.log('🔄 MapPicker: Retrying static map fetch');
      setMapError(null);
      setMapData(null);
      // Delay refresh to ensure error state is cleared
      setTimeout(() => {
        setMapRefreshKey(prev => {
          const newKey = prev + 1;
          console.log('🔄 MapPicker: Retry with refresh key:', newKey);
          return newKey;
        });
      }, 100);
    }
  };

  // Helper function to convert pixel coordinates to lat/lng using proper Web Mercator projection
  const pixelToLatLng = (pixelX, pixelY, centerLat, centerLng, zoom, mapWidth, mapHeight) => {
    // Google Maps uses a fixed tile size of 256 pixels
    const TILE_SIZE = 256;
    
    // Calculate the scale factor for this zoom level
    const scale = Math.pow(2, zoom);
    
    // Calculate the offset in pixels from the center of the map
    const offsetX = pixelX - mapWidth / 2;
    const offsetY = pixelY - mapHeight / 2;
    
    // Convert pixel offset to world coordinate offset
    const worldOffsetX = offsetX / scale;
    const worldOffsetY = offsetY / scale;
    
    // Calculate new longitude (simple linear conversion)
    const lng = centerLng + (worldOffsetX * 360) / TILE_SIZE;
    
    // Calculate new latitude (uses Mercator projection)
    const centerLatRad = centerLat * Math.PI / 180;
    const centerWorldY = Math.log(Math.tan(Math.PI / 4 + centerLatRad / 2));
    
    const newWorldY = centerWorldY - (worldOffsetY * 2 * Math.PI) / TILE_SIZE;
    const lat = (2 * Math.atan(Math.exp(newWorldY)) - Math.PI / 2) * 180 / Math.PI;
    
    // Clamp to valid coordinate ranges
    const clampedLat = Math.max(-85, Math.min(85, lat));
    const clampedLng = Math.max(-180, Math.min(180, lng));
    
    return { lat: clampedLat, lng: clampedLng };
  };

  // Helper function to convert lat/lng to pixel coordinates
  const latLngToPixel = (lat, lng, centerLat, centerLng, zoom, mapWidth, mapHeight) => {
    const TILE_SIZE = 256;
    const scale = Math.pow(2, zoom);
    
    // Convert longitude difference to pixel offset
    const offsetX = ((lng - centerLng) * TILE_SIZE * scale) / 360;
    
    // Convert latitude difference to pixel offset using Mercator projection
    const latRad = lat * Math.PI / 180;
    const centerLatRad = centerLat * Math.PI / 180;
    
    const latWorld = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
    const centerLatWorld = Math.log(Math.tan(Math.PI / 4 + centerLatRad / 2));
    
    const offsetY = ((centerLatWorld - latWorld) * TILE_SIZE * scale) / (2 * Math.PI);
    
    const pixelX = mapWidth / 2 + offsetX;
    const pixelY = mapHeight / 2 + offsetY;
    
    return { x: pixelX, y: pixelY };
  };

  // Helper function to calculate pixel distance between two points
  const calculatePixelDistance = (point1, point2) => {
    const deltaX = point2.x - point1.x;
    const deltaY = point2.y - point1.y;
    return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
  };

  if (!hasValidCoordinates) {
    return (
      <Box
        w={MAP_WIDTH}
        h={MAP_HEIGHT}
        bg="gray.700"
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="2px dashed"
        borderColor="gray.500"
      >
        <VStack spacing={2}>
          <Text color="gray.400" fontSize="sm" textAlign="center">
            No Valid Coordinates
          </Text>
          <Text color="gray.500" fontSize="xs" textAlign="center">
            Search for an address above to display the map
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <VStack spacing={2} align="stretch">
      {/* Edit Mode Toggle */}
      <HStack justify="space-between" align="center">
        <Button
          size="sm"
          colorScheme={isEditMode ? "orange" : "blue"}
          variant={isEditMode ? "solid" : "outline"}
          onClick={() => setIsEditMode(!isEditMode)}
          leftIcon={<EditIcon />}
          isDisabled={isUpdatingCoordinates}
        >
          {isEditMode ? "Exit Edit Mode" : "Edit Location"}
        </Button>
          
        {isEditMode && (
          <HStack spacing={4}>
            <Text fontSize="xs" color="orange.400">
              {geofenceType === "circular" && "Click anywhere on map to move pin or radius edge"}
              {geofenceType === "polygon" && isCreatingPolygon && "Click points to create polygon"}
              {geofenceType === "none" && "Click anywhere on map to move pin"}
            </Text>
            {isUpdatingCoordinates && (
              <HStack spacing={2}>
                <Spinner size="xs" color="orange.400" />
                <Text fontSize="xs" color="orange.400">
                  Updating location...
                </Text>
              </HStack>
            )}
          </HStack>
        )}
      </HStack>

      <Box position="relative" w={MAP_WIDTH} h={MAP_HEIGHT}>
        {mapLoading && (
          <Center
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="gray.700"
            borderRadius="md"
            zIndex={2}
          >
            <VStack spacing={2}>
              <Spinner color="blue.400" />
              <Text color="gray.400" fontSize="sm">Loading map...</Text>
            </VStack>
          </Center>
        )}

        {mapError && !mapLoading && (
          <Center
            w="full"
            h="full"
            bg="gray.700"
            borderRadius="md"
            border="2px solid"
            borderColor="red.500"
          >
            <VStack spacing={3}>
              <Text color="red.400" fontSize="sm" textAlign="center">
                Failed to load map
              </Text>
              <Text color="gray.400" fontSize="xs" textAlign="center">
                {mapError.message || 'Unknown error'}
              </Text>
              <Button size="sm" colorScheme="blue" onClick={handleRetry}>
                Retry
              </Button>
            </VStack>
          </Center>
        )}

        {!mapError && mapData && (
          <Box
            data-map-container
            position="relative"
            w="full"
            h="full"
            borderRadius="md"
            overflow="hidden"
            cursor={
              isUpdatingCoordinates ? "wait" :
              isEditMode && !isCreatingPolygon ? "crosshair" : 
              "default"
            }
            onClick={handleMapClick}
            border={isEditMode ? "2px solid" : "1px solid"}
            borderColor={
              isUpdatingCoordinates ? "green.400" :
              isEditMode ? "orange.400" : "gray.600"
            }
            _hover={
              isEditMode && !isCreatingPolygon && !isUpdatingCoordinates ? {
                borderColor: "orange.300",
                boxShadow: "0 0 0 1px orange.300"
              } : {}
            }
          >
            {/* Map image */}
            <Box
              as="img"
              w="full"
              h="full"
              objectFit="cover"
              src={mapData}
              alt="Location map"
              draggable={false}
              userSelect="none"
              key={`map-${latitude}-${longitude}-${mapZoom}-${mapRefreshKey}`}
            />

            {/* Canvas overlay for polygon drawing */}
            {geofenceType === "polygon" && (
              <Box
                as="canvas"
                position="absolute"
                top={0}
                left={0}
                w="full"
                h="full"
                pointerEvents="none"
              />
            )}

            {/* Zoom controls */}
            <VStack
              position="absolute"
              top={2}
              right={2}
              spacing={1}
            >
              <IconButton
                size="sm"
                colorScheme="blackAlpha"
                bg="blackAlpha.800"
                color="white"
                _hover={{ bg: "blackAlpha.700" }}
                icon={<Text fontSize="lg" fontWeight="bold">+</Text>}
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoomIn();
                }}
                isDisabled={mapZoom >= 20}
              />
              <IconButton
                size="sm"
                colorScheme="blackAlpha"
                bg="blackAlpha.800"
                color="white"
                _hover={{ bg: "blackAlpha.700" }}
                icon={<Text fontSize="lg" fontWeight="bold">-</Text>}
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoomOut();
                }}
                isDisabled={mapZoom <= 1}
              />
            </VStack>

            {/* Enhanced Geofence Circle */}
            {geofenceType === "circular" && geofenceRadius && hasValidCoordinates && (
              <>
                {/* Main circle */}
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  w={`${calculateRadiusPixels(geofenceRadius, latitude, mapZoom) * 2}px`}
                  h={`${calculateRadiusPixels(geofenceRadius, latitude, mapZoom) * 2}px`}
                  border="2px solid"
                  borderColor="blue.400"
                  borderRadius="full"
                  bg="blue.100"
                  opacity={0.3}
                  pointerEvents="none"
                  transition="all 0.2s ease"
                  key={`circle-${latitude}-${longitude}-${geofenceRadius}-${mapZoom}`}
                />
                
                {/* Radius adjustment handle */}
                {isEditMode && (
                  <Box
                    position="absolute"
                    top="50%"
                    left={`calc(50% + ${calculateRadiusPixels(geofenceRadius, latitude, mapZoom)}px)`}
                    transform="translate(-50%, -50%)"
                    w={4}
                    h={4}
                    bg="blue.500"
                    borderRadius="full"
                    border="2px solid"
                    borderColor="white"
                    boxShadow="lg"
                    cursor="pointer"
                    _hover={{ 
                      bg: "blue.600",
                      w: 5,
                      h: 5,
                      boxShadow: "xl"
                    }}
                    onClick={handleRadiusAdjustClick}
                    zIndex={3}
                    key={`handle-${latitude}-${longitude}-${geofenceRadius}-${mapZoom}`}
                  />
                )}
                
                {/* Radius value label */}
                <Box
                  position="absolute"
                  top="50%"
                  left={`calc(50% + ${calculateRadiusPixels(geofenceRadius, latitude, mapZoom) / 2}px)`}
                  transform="translate(-50%, -50%)"
                  bg="blackAlpha.700"
                  color="white"
                  px={2}
                  py={1}
                  borderRadius="md"
                  fontSize="xs"
                  fontWeight="bold"
                  pointerEvents="none"
                  zIndex={2}
                  key={`label-${latitude}-${longitude}-${geofenceRadius}-${mapZoom}`}
                >
                  {geofenceRadius}m
                </Box>
              </>
            )}

            {/* Traditional Pin Marker */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -100%)"
              pointerEvents="none"
              zIndex={4}
              transition="all 0.2s ease"
              key={`pin-marker-${latitude}-${longitude}-${isUpdatingCoordinates}-${isEditMode}`}
            >
              {/* Pin Body */}
              <Box
                w={isUpdatingCoordinates ? 8 : isEditMode ? 7 : 6}
                h={isUpdatingCoordinates ? 8 : isEditMode ? 7 : 6}
                bg={
                  isUpdatingCoordinates ? "green.500" :
                  isEditMode ? "red.400" : "red.500"
                }
                borderRadius="50% 50% 50% 0"
                border="2px solid"
                borderColor="white"
                boxShadow={isUpdatingCoordinates ? "2xl" : "md"}
                transform="rotate(-45deg)"
                position="relative"
              >
                {/* Pin Center Dot */}
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  w="30%"
                  h="30%"
                  bg="white"
                  borderRadius="full"
                />
              </Box>
            </Box>

            {/* Location update feedback */}
            {showLocationUpdate && (
              <Box
                position="absolute"
                bottom={2}
                right={2}
                bg="green.500"
                color="white"
                px={3}
                py={2}
                borderRadius="md"
                fontSize="sm"
                fontWeight="semibold"
                boxShadow="lg"
                zIndex={3}
                pointerEvents="none"
              >
                Location Updated!
              </Box>
            )}

            {/* Crosshairs when in edit mode */}
            {isEditMode && !isEditMode && (
              <>
                {/* Horizontal crosshair */}
                <Box
                  position="absolute"
                  top="50%"
                  left={0}
                  right={0}
                  height="1px"
                  bg="orange.400"
                  opacity={0.6}
                  transform="translateY(-50%)"
                  pointerEvents="none"
                  zIndex={1}
                />
                
                {/* Vertical crosshair */}
                <Box
                  position="absolute"
                  left="50%"
                  top={0}
                  bottom={0}
                  width="1px"
                  bg="orange.400"
                  opacity={0.6}
                  transform="translateX(-50%)"
                  pointerEvents="none"
                  zIndex={1}
                />
              </>
            )}

            {/* Polygon rendering */}
            {geofenceType === "polygon" && (
              <>
                {/* Existing polygon points */}
                {geofencePolygon.map((point, index) => {
                  const pixel = latLngToPixel(point.lat, point.lng, latitude, longitude, mapZoom, MAP_WIDTH, MAP_HEIGHT);
                  const isFirstPoint = index === 0;
                  const allPoints = [...geofencePolygon, ...tempPolygonPoints];
                  const canAutoClose = isCreatingPolygon && allPoints.length >= 3 && isFirstPoint;
                  
                  return (
                    <Box
                      key={index}
                      position="absolute"
                      left={`${pixel.x}px`}
                      top={`${pixel.y}px`}
                      transform="translate(-50%, -50%)"
                      w={canAutoClose ? 4 : 3}
                      h={canAutoClose ? 4 : 3}
                      bg={canAutoClose ? "green.400" : "purple.500"}
                      borderRadius="full"
                      border="2px solid"
                      borderColor="white"
                      boxShadow={canAutoClose ? "lg" : "sm"}
                      zIndex={3}
                      _hover={canAutoClose ? { w: 5, h: 5, bg: "green.300" } : {}}
                      cursor={canAutoClose ? "pointer" : "default"}
                    />
                  );
                })}
                
                {/* Temporary polygon points */}
                {tempPolygonPoints.map((point, index) => {
                  const pixel = latLngToPixel(point.lat, point.lng, latitude, longitude, mapZoom, MAP_WIDTH, MAP_HEIGHT);
                  const isFirstTempPoint = index === 0 && geofencePolygon.length === 0;
                  const allPoints = [...geofencePolygon, ...tempPolygonPoints];
                  const canAutoClose = isCreatingPolygon && allPoints.length >= 3 && isFirstTempPoint;
                  
                  return (
                    <Box
                      key={`temp-${index}`}
                      position="absolute"
                      left={`${pixel.x}px`}
                      top={`${pixel.y}px`}
                      transform="translate(-50%, -50%)"
                      w={canAutoClose ? 4 : 3}
                      h={canAutoClose ? 4 : 3}
                      bg={canAutoClose ? "green.300" : "purple.300"}
                      borderRadius="full"
                      border="2px solid"
                      borderColor="white"
                      boxShadow={canAutoClose ? "lg" : "sm"}
                      zIndex={3}
                      _hover={canAutoClose ? { w: 5, h: 5, bg: "green.200" } : {}}
                      cursor={canAutoClose ? "pointer" : "default"}
                    />
                  );
                })}
                
                {/* Polygon lines */}
                <svg
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 2
                  }}
                >
                  {/* Draw existing polygon */}
                  {geofencePolygon.length > 1 && (
                    <polygon
                      points={geofencePolygon.map(point => {
                        const pixel = latLngToPixel(point.lat, point.lng, latitude, longitude, mapZoom, MAP_WIDTH, MAP_HEIGHT);
                        return `${pixel.x},${pixel.y}`;
                      }).join(' ')}
                      fill="rgba(147, 51, 234, 0.2)"
                      stroke="rgb(147, 51, 234)"
                      strokeWidth="2"
                    />
                  )}
                  
                  {/* Draw temporary polygon lines */}
                  {tempPolygonPoints.length > 1 && (
                    <polyline
                      points={tempPolygonPoints.map(point => {
                        const pixel = latLngToPixel(point.lat, point.lng, latitude, longitude, mapZoom, MAP_WIDTH, MAP_HEIGHT);
                        return `${pixel.x},${pixel.y}`;
                      }).join(' ')}
                      fill="none"
                      stroke="rgb(196, 181, 253)"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                    />
                  )}
                  
                  {/* Draw closing line preview when polygon can be auto-closed */}
                  {isCreatingPolygon && (() => {
                    const allPoints = [...geofencePolygon, ...tempPolygonPoints];
                    if (allPoints.length >= 3) {
                      const firstPoint = allPoints[0];
                      const lastPoint = allPoints[allPoints.length - 1];
                      const firstPixel = latLngToPixel(firstPoint.lat, firstPoint.lng, latitude, longitude, mapZoom, MAP_WIDTH, MAP_HEIGHT);
                      const lastPixel = latLngToPixel(lastPoint.lat, lastPoint.lng, latitude, longitude, mapZoom, MAP_WIDTH, MAP_HEIGHT);
                      
                      return (
                        <line
                          x1={lastPixel.x}
                          y1={lastPixel.y}
                          x2={firstPixel.x}
                          y2={firstPixel.y}
                          stroke="rgb(34, 197, 94)"
                          strokeWidth="2"
                          strokeDasharray="10,5"
                          opacity="0.7"
                        />
                      );
                    }
                    return null;
                  })()}
                </svg>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Coordinate display and controls */}
      <HStack justify="space-between" align="center" fontSize="sm">
        <Text color="gray.400">
          Coordinates: {latitude?.toFixed(6)}, {longitude?.toFixed(6)}
        </Text>
        <Text color="gray.400">
          Zoom: {mapZoom}
        </Text>
      </HStack>

      {/* Polygon creation controls */}
      {geofenceType === "polygon" && isCreatingPolygon && (
        <HStack spacing={2} p={3} bg="purple.900" borderRadius="md" border="1px solid" borderColor="purple.600">
          <VStack align="start" spacing={1} flex={1}>
            <Text fontSize="sm" color="purple.200">
              Creating polygon: {(() => {
                const allPoints = [...geofencePolygon, ...tempPolygonPoints];
                return allPoints.length;
              })()} points
              {(() => {
                const allPoints = [...geofencePolygon, ...tempPolygonPoints];
                return allPoints.length >= 3 ? " (minimum met)" : " (need at least 3)";
              })()}
            </Text>
            {(() => {
              const allPoints = [...geofencePolygon, ...tempPolygonPoints];
              return allPoints.length >= 3 && (
                <Text fontSize="xs" color="green.300">
                  💡 Click on the first point (highlighted in green) to close the polygon
                </Text>
              );
            })()}
          </VStack>
          <Button
            size="sm"
            colorScheme="purple"
            onClick={onPolygonComplete}
            isDisabled={(() => {
              const allPoints = [...geofencePolygon, ...tempPolygonPoints];
              return allPoints.length < 3;
            })()}
          >
            Complete
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            onClick={onPolygonCancel}
          >
            Cancel
          </Button>
        </HStack>
      )}

    </VStack>
  );
}

export default function ClinicalSitesManagement() {
  const navigate = useNavigate();
  const toast = useToast();
  const user = useUser(api);

  // State Management
  const [searchTerm, setSearchTerm] = useState("");
  const [facilityTypeFilter, setFacilityTypeFilter] = useState("");
  const [activeStatusFilter, setActiveStatusFilter] = useState("");
  const [selectedInstitution, setSelectedInstitution] = useState("");
  const [editingSite, setEditingSite] = useState(null);
  const [deletingSite, setDeletingSite] = useState(null);
  const [expandedAgencies, setExpandedAgencies] = useState(new Set());

  // Form Data State
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    latitude: null,
    longitude: null,
    siteContactPerson: "",
    sitePhone: "",
    siteEmail: "",
    units: [],
    notes: "",
    isActive: true,
    agencyId: "",
    geofenceType: "none",
    geofenceRadius: 100,
    geofencePolygon: []
  });

  const [formErrors, setFormErrors] = useState({});

  // Modal Controls
  const { 
    isOpen: isCreateModalOpen, 
    onOpen: onCreateModalOpen, 
    onClose: onCreateModalClose 
  } = useDisclosure();
  
  const { 
    isOpen: isEditModalOpen, 
    onOpen: onEditModalOpen, 
    onClose: onEditModalClose 
  } = useDisclosure();
  
  const { 
    isOpen: isDeleteModalOpen, 
    onOpen: onDeleteModalOpen, 
    onClose: onDeleteModalClose 
  } = useDisclosure();

  // API Hooks
  const [{ data: institutions, error: institutionsError, fetching: institutionsFetching }] = useFindMany(api.institutionConfiguration, {
    select: {
      id: true,
      institution: true,
      brandingSettings: true,
      enableFisdapShiftProcessing: true,
      enableShiftScheduling: true,
      enablePatientEncounterTracking: true,
      enablePreceptorEvaluations: true,
      cohortManagementEnabled: true,
      enableCalendarIntegration: true,
      enableClinicalQuickNotes: true,
      enablePreceptorScoring: true
    },
    sort: { institution: "Ascending" }
  });

  // Filter for agencies based on selected institution
  const getAgencyFilter = useCallback(() => {
    if (!selectedInstitution) return { isActive: { equals: true } };
    
    return {
      AND: [
        { isActive: { equals: true } },
        { institutionalAffiliations: { matches: selectedInstitution } }
      ]
    };
  }, [selectedInstitution]);

  const [{ data: sites, error: sitesError, fetching: sitesFetching }, refetchSites] = useFindMany(api.clinicalSite, {
    select: {
      id: true,
      name: true,
      address: true,
      latitude: true,
      longitude: true,
      siteContactPerson: true,
      sitePhone: true,
      siteEmail: true,
      units: true,
      notes: { markdown: true },
      isActive: true,
      needsGeofenceSetup: true,
      autoCreatedFromSync: true,
      geofenceType: true,
      geofenceRadius: true,
      geofencePolygon: true,
      createdAt: true,
      updatedAt: true,
      agency: {
        id: true,
        name: true,
        facilityType: true,
        institutionalAffiliations: true
      },
      createdBy: {
        id: true,
        firstName: true,
        lastName: true,
        email: true
      }
    },
    sort: { name: "Ascending" }
  });

  const [{ data: agencies, error: agenciesError, fetching: agenciesFetching }] = useFindMany(api.agency, {
    select: {
      id: true,
      name: true,
      facilityType: true,
      isActive: true,
      institutionalAffiliations: true
    },
    filter: getAgencyFilter(),
    sort: { name: "Ascending" }
  });

  // Action Hooks
  const [{ data: createResult, error: createError, fetching: createFetching }, createSite] = useAction(api.clinicalSite.create);
  const [{ data: updateResult, error: updateError, fetching: updateFetching }, updateSite] = useAction(api.clinicalSite.update);
  const [{ data: deleteResult, error: deleteError, fetching: deleteFetching }, deleteSite] = useAction(api.clinicalSite.delete);

  // User Permissions
  const hasSchedulingAccess = useCallback(() => {
    if (!user) return false;
    
    // Special email check for backwards compatibility
    if (user?.email === '74spartan@gmail.com') {
      return true;
    }
    
    // Check if user has system-level roles
    if (user?.roles && Array.isArray(user.roles)) {
      const adminRoleNames = ['admin', 'sysadmin', 'system-admin'];
      const hasSystemRole = user.roles.some(role => {
        if (typeof role === 'string') {
          return adminRoleNames.includes(role);
        } else if (typeof role === 'object' && role !== null) {
          return adminRoleNames.includes(role.key) || adminRoleNames.includes(role.name);
        }
        return false;
      });
      
      if (hasSystemRole) return true;
    }
    
    // Check for scheduler role in institutionRole
    if (user?.institutionRole && Array.isArray(user.institutionRole)) {
      return user.institutionRole.includes('scheduler') || user.institutionRole.includes('instructor');
    }
    
    return false;
  }, [user]);

  const isSystemAdmin = useCallback(() => {
    if (!user) return false;
    
    // Special email check for backwards compatibility
    if (user?.email === '74spartan@gmail.com') {
      return true;
    }
    
    // Check if user has system-level admin roles
    if (user?.roles && Array.isArray(user.roles)) {
      const systemAdminRoleNames = ['sysadmin', 'system-admin'];
      return user.roles.some(role => {
        if (typeof role === 'string') {
          return systemAdminRoleNames.includes(role);
        } else if (typeof role === 'object' && role !== null) {
          return systemAdminRoleNames.includes(role.key) || systemAdminRoleNames.includes(role.name);
        }
        return false;
      });
    }
    
    return false;
  }, [user]);

  // Available institutions for selection
  const getAvailableInstitutions = useCallback(() => {
    if (!institutions || !Array.isArray(institutions)) return [];
    
    if (isSystemAdmin()) {
      // System admins can see all institutions
      return institutions;
    } else if (user?.institution) {
      // Other users only see their institution
      return institutions.filter(inst => inst.institution === user.institution);
    }
    
    return [];
  }, [institutions, isSystemAdmin, user]);

  const availableInstitutions = getAvailableInstitutions();

  // Set default institution on user load
  useEffect(() => {
    if (user && !selectedInstitution) {
      if (isSystemAdmin()) {
        // System admins default to first institution or empty for all
        if (institutions?.length > 0) {
          setSelectedInstitution(""); // Empty means show all for system admins
        }
      } else if (user.institution) {
        // Regular users default to their institution
        setSelectedInstitution(user.institution);
      }
    }
  }, [user, institutions, selectedInstitution, isSystemAdmin]);

  // Form Validation Function
  const validateForm = useCallback(() => {
    const errors = {};

    // Required field validations
    if (!formData.name?.trim()) {
      errors.name = "Site name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Site name must be at least 2 characters";
    }

    if (!formData.agencyId) {
      errors.agencyId = "Agency is required";
    }

    if (!formData.siteContactPerson?.trim()) {
      errors.siteContactPerson = "Site contact person is required";
    }

    // Optional phone validation - only validate format if provided
    if (formData.sitePhone?.trim() && !/^\+?[\d\s\-\(\)]+$/.test(formData.sitePhone.trim())) {
      errors.sitePhone = "Please enter a valid phone number";
    }

    // Optional email validation - only validate format if provided
    if (formData.siteEmail?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.siteEmail.trim())) {
      errors.siteEmail = "Please enter a valid email address";
    }

    // Coordinate validation
    if (formData.latitude === null || formData.latitude === undefined || isNaN(formData.latitude)) {
      errors.coordinates = "Valid coordinates are required. Please search for an address.";
    } else if (formData.latitude < -90 || formData.latitude > 90) {
      errors.coordinates = "Latitude must be between -90 and 90";
    }

    if (formData.longitude === null || formData.longitude === undefined || isNaN(formData.longitude)) {
      if (!errors.coordinates) {
        errors.coordinates = "Valid coordinates are required. Please search for an address.";
      }
    } else if (formData.longitude < -180 || formData.longitude > 180) {
      errors.coordinates = "Longitude must be between -180 and 180";
    }

    // Geofence validation
    if (formData.geofenceType === "circular") {
      if (!formData.geofenceRadius || formData.geofenceRadius < 10) {
        errors.geofenceRadius = "Radius must be at least 10 meters";
      } else if (formData.geofenceRadius > 10000) {
        errors.geofenceRadius = "Radius cannot exceed 10,000 meters";
      }
    }

    if (formData.geofenceType === "polygon") {
      if (!formData.geofencePolygon || formData.geofencePolygon.length < 3) {
        errors.geofencePolygon = "Polygon must have at least 3 points";
      }
    }

    const isValid = Object.keys(errors).length === 0;
    return { errors, isValid };
  }, [formData]);

  // Form Reset Function
  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      address: "",
      latitude: null,
      longitude: null,
      siteContactPerson: "",
      sitePhone: "",
      siteEmail: "",
      units: [],
      notes: "",
      isActive: true,
      agencyId: "",
      geofenceType: "none",
      geofenceRadius: 100,
      geofencePolygon: []
    });
    setFormErrors({});
  }, []);

  // Data Processing Functions
  const processFormDataForSubmission = useCallback(() => {
    const processedData = {
      ...formData,
      name: formData.name?.trim(),
      address: formData.address?.trim(),
      siteContactPerson: formData.siteContactPerson?.trim(),
      sitePhone: formData.sitePhone?.trim(),
      siteEmail: formData.siteEmail?.trim().toLowerCase(),
      notes: formData.notes?.trim() ? { markdown: formData.notes.trim() } : null,
      latitude: parseFloat(formData.latitude),
      longitude: parseFloat(formData.longitude),
      units: Array.isArray(formData.units) ? formData.units : [],
      isActive: Boolean(formData.isActive),
      geofenceRadius: formData.geofenceType === "circular" ? parseInt(formData.geofenceRadius) : null,
      geofencePolygon: formData.geofenceType === "polygon" ? formData.geofencePolygon : []
    };

    // Remove null/undefined geofence data based on type
    if (formData.geofenceType === "none") {
      processedData.geofenceRadius = null;
      processedData.geofencePolygon = [];
    } else if (formData.geofenceType === "circular") {
      processedData.geofencePolygon = [];
    } else if (formData.geofenceType === "polygon") {
      processedData.geofenceRadius = null;
    }

    // Convert agency relationship to proper GraphQL format
    if (formData.agencyId) {
      delete processedData.agencyId;
      processedData.agency = { _link: formData.agencyId };
    }

    return processedData;
  }, [formData]);

  // Filter and Search Functions
  const getFilteredAndSortedSites = useCallback(() => {
    if (!sites || !Array.isArray(sites)) {
      return [];
    }

    let filtered = sites.filter(site => {
      // Institution filter - only show sites for agencies in selected institution
      if (selectedInstitution && site.agency?.institutionalAffiliations) {
        if (!Array.isArray(site.agency.institutionalAffiliations)) {
          return false;
        }
        if (!site.agency.institutionalAffiliations.includes(selectedInstitution)) {
          return false;
        }
      } else if (selectedInstitution && !site.agency?.institutionalAffiliations) {
        return false;
      }

      // Search filter
      if (searchTerm?.trim()) {
        const search = searchTerm.toLowerCase();
        const matchesName = site.name?.toLowerCase().includes(search);
        const matchesAddress = site.address?.toLowerCase().includes(search);
        const matchesContact = site.siteContactPerson?.toLowerCase().includes(search);
        const matchesAgency = site.agency?.name?.toLowerCase().includes(search);
        
        if (!matchesName && !matchesAddress && !matchesContact && !matchesAgency) {
          return false;
        }
      }

      // Facility type filter
      if (facilityTypeFilter && facilityTypeFilter !== "") {
        if (site.agency?.facilityType !== facilityTypeFilter) {
          return false;
        }
      }

      // Active status filter
      if (activeStatusFilter && activeStatusFilter !== "") {
        const isActive = activeStatusFilter === "true";
        if (site.isActive !== isActive) {
          return false;
        }
      }

      return true;
    });

    // Sort by name
    filtered.sort((a, b) => {
      const nameA = a.name?.toLowerCase() || "";
      const nameB = b.name?.toLowerCase() || "";
      return nameA.localeCompare(nameB);
    });

    return filtered;
  }, [sites, searchTerm, facilityTypeFilter, activeStatusFilter, selectedInstitution]);

  const filteredSites = getFilteredAndSortedSites();

  // Group sites by agency for hierarchical view
  const getGroupedSitesByAgency = useCallback(() => {
    if (!agencies || !Array.isArray(agencies)) return [];
    
    // Filter agencies by selected institution first
    let filteredAgencies = agencies.filter(agency => {
      if (!selectedInstitution) {
        return true; // Show all if no institution selected (for system admins)
      }
      
      if (!agency.institutionalAffiliations || !Array.isArray(agency.institutionalAffiliations)) {
        return false;
      }
      
      return agency.institutionalAffiliations.includes(selectedInstitution);
    });
    
    // Create a map of agency ID to sites
    const sitesByAgency = new Map();
    const filteredSitesList = getFilteredAndSortedSites();
    
    // Initialize filtered agencies with empty arrays
    filteredAgencies.forEach(agency => {
      sitesByAgency.set(agency.id, {
        agency,
        sites: []
      });
    });
    
    // Group filtered sites by their agency
    filteredSitesList.forEach(site => {
      if (site.agency?.id) {
        const agencyData = sitesByAgency.get(site.agency.id);
        if (agencyData) {
          agencyData.sites.push(site);
        }
      }
    });
    
    // Convert to array and filter based on search/filter criteria
    let groupedData = Array.from(sitesByAgency.values());
    
    // If we have search/filter criteria, only show agencies that have matching sites
    // or agencies that match the search term themselves
    if (searchTerm?.trim() || facilityTypeFilter || activeStatusFilter) {
      groupedData = groupedData.filter(({ agency, sites }) => {
        // Always include if agency has matching sites
        if (sites.length > 0) return true;
        
        // Also include if agency name matches search (even with no sites)
        if (searchTerm?.trim()) {
          const search = searchTerm.toLowerCase();
          if (agency.name?.toLowerCase().includes(search)) {
            return true;
          }
        }
        
        // Include if agency matches facility type filter (even with no sites)
        if (facilityTypeFilter && agency.facilityType === facilityTypeFilter) {
          return true;
        }
        
        return false;
      });
    }
    
    // Sort agencies by name
    groupedData.sort((a, b) => {
      const nameA = a.agency.name?.toLowerCase() || "";
      const nameB = b.agency.name?.toLowerCase() || "";
      return nameA.localeCompare(nameB);
    });
    
    return groupedData;
  }, [agencies, getFilteredAndSortedSites, searchTerm, facilityTypeFilter, activeStatusFilter, selectedInstitution]);

  const groupedSites = getGroupedSitesByAgency();

  // Agency expand/collapse handlers
  const toggleAgencyExpansion = useCallback((agencyId) => {
    setExpandedAgencies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agencyId)) {
        newSet.delete(agencyId);
      } else {
        newSet.add(agencyId);
      }
      return newSet;
    });
  }, []);

  const expandAllAgencies = useCallback(() => {
    const allAgencyIds = groupedSites.map(({ agency }) => agency.id);
    setExpandedAgencies(new Set(allAgencyIds));
  }, [groupedSites]);

  const collapseAllAgencies = useCallback(() => {
    setExpandedAgencies(new Set());
  }, []);

  // Address Selection Handler
  const handleAddressSelect = useCallback((addressData) => {
    setFormData(prev => ({
      ...prev,
      address: addressData.formatted_address,
      latitude: addressData.latitude,
      longitude: addressData.longitude
    }));

    // Clear coordinate-related errors
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.coordinates;
      return newErrors;
    });
  }, []);

  // Coordinate Change Handler
  const handleCoordinatesChange = useCallback((lat, lng) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));

    // Clear coordinate-related errors
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.coordinates;
      return newErrors;
    });
  }, []);

  // Modal Management Handlers
  const handleCreateSite = useCallback(() => {
    if (!hasSchedulingAccess()) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to create clinical sites.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!selectedInstitution && !isSystemAdmin()) {
      toast({
        title: "Institution Required",
        description: "Please select an institution first.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    resetForm();
    onCreateModalOpen();
  }, [hasSchedulingAccess, selectedInstitution, isSystemAdmin, resetForm, onCreateModalOpen, toast]);

  const handleCreateSiteForAgency = useCallback((agencyId) => {
    if (!hasSchedulingAccess()) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to create clinical sites.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!selectedInstitution && !isSystemAdmin()) {
      toast({
        title: "Institution Required",
        description: "Please select an institution first.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    resetForm();
    // Pre-select the agency
    setFormData(prev => ({
      ...prev,
      agencyId: agencyId
    }));
    onCreateModalOpen();
  }, [hasSchedulingAccess, selectedInstitution, isSystemAdmin, resetForm, onCreateModalOpen, toast]);

  const handleEditSite = useCallback((site) => {
    if (!hasSchedulingAccess()) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to edit clinical sites.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setEditingSite(site);
    setFormData({
      name: site.name || "",
      address: site.address || "",
      latitude: site.latitude,
      longitude: site.longitude,
      siteContactPerson: site.siteContactPerson || "",
      sitePhone: site.sitePhone || "",
      siteEmail: site.siteEmail || "",
      units: Array.isArray(site.units) ? site.units : [],
      notes: site.notes || "",
      isActive: Boolean(site.isActive),
      agencyId: site.agency?.id || "",
      geofenceType: site.geofenceType || "none",
      geofenceRadius: site.geofenceRadius || 100,
      geofencePolygon: Array.isArray(site.geofencePolygon) ? site.geofencePolygon : []
    });
    setFormErrors({});
    onEditModalOpen();
  }, [hasSchedulingAccess, onEditModalOpen, toast]);

  const handleDeleteSite = useCallback((site) => {
    if (!hasSchedulingAccess()) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to delete clinical sites.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setDeletingSite(site);
    onDeleteModalOpen();
  }, [hasSchedulingAccess, onDeleteModalOpen, toast]);

  const handleCloseModals = useCallback(() => {
    onCreateModalClose();
    onEditModalClose();
    onDeleteModalClose();
    setEditingSite(null);
    setDeletingSite(null);
    resetForm();
  }, [onCreateModalClose, onEditModalClose, onDeleteModalClose, resetForm]);

  // Form Submission Handlers
  const handleSubmitCreate = useCallback(async () => {
    const validation = validateForm();
    setFormErrors(validation.errors);

    if (!validation.isValid) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form before submitting.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const processedData = processFormDataForSubmission();
      const result = await createSite(processedData);

      if (result?.error) {
        throw new Error(result.error.message || "Failed to create clinical site");
      }

      toast({
        title: "Success",
        description: "Clinical site created successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      await refetchSites();
      handleCloseModals();
    } catch (error) {
      console.error("Create site error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create clinical site. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [validateForm, processFormDataForSubmission, createSite, toast, refetchSites, handleCloseModals]);

  const handleSubmitUpdate = useCallback(async () => {
    if (!editingSite?.id) {
      toast({
        title: "Error",
        description: "No site selected for editing.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const validation = validateForm();
    setFormErrors(validation.errors);

    if (!validation.isValid) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form before submitting.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const processedData = processFormDataForSubmission();
      const result = await updateSite({id: editingSite.id, ...processedData});

      if (result?.error) {
        throw new Error(result.error.message || "Failed to update clinical site");
      }

      toast({
        title: "Success",
        description: "Clinical site updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      await refetchSites();
      handleCloseModals();
    } catch (error) {
      console.error("Update site error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update clinical site. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [editingSite, validateForm, processFormDataForSubmission, updateSite, toast, refetchSites, handleCloseModals]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingSite?.id) {
      toast({
        title: "Error",
        description: "No site selected for deletion.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const result = await deleteSite(deletingSite.id);

      if (result?.error) {
        throw new Error(result.error.message || "Failed to delete clinical site");
      }

      toast({
        title: "Success",
        description: "Clinical site deleted successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      await refetchSites();
      handleCloseModals();
    } catch (error) {
      console.error("Delete site error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete clinical site. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [deletingSite, deleteSite, toast, refetchSites, handleCloseModals]);

  // Geofencing Handlers
  const handleGeofenceTypeChange = useCallback((type) => {
    setFormData(prev => ({
      ...prev,
      geofenceType: type,
      geofenceRadius: type === "circular" ? (prev.geofenceRadius || 100) : 100,
      geofencePolygon: type === "polygon" ? prev.geofencePolygon : []
    }));

    // Clear related errors
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.geofenceRadius;
      delete newErrors.geofencePolygon;
      return newErrors;
    });
  }, []);

  const handleGeofenceRadiusChange = useCallback((radius) => {
    const numericRadius = parseInt(radius) || 100;
    setFormData(prev => ({
      ...prev,
      geofenceRadius: numericRadius
    }));

    // Clear radius error if valid
    if (numericRadius >= 10 && numericRadius <= 10000) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.geofenceRadius;
        return newErrors;
      });
    }
  }, []);

  // Polygon creation state
  const [isCreatingPolygon, setIsCreatingPolygon] = useState(false);
  const [tempPolygonPoints, setTempPolygonPoints] = useState([]);

  const handlePolygonPointAdd = useCallback((point) => {
    setTempPolygonPoints(prev => [...prev, point]);
  }, []);

  const handlePolygonComplete = useCallback(() => {
    if (tempPolygonPoints.length < 3) {
      toast({
        title: "Invalid Polygon",
        description: "A polygon must have at least 3 points.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      geofencePolygon: [...tempPolygonPoints]
    }));

    setIsCreatingPolygon(false);
    setTempPolygonPoints([]);

    // Clear polygon error
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.geofencePolygon;
      return newErrors;
    });

    toast({
      title: "Success",
      description: `Polygon completed with ${tempPolygonPoints.length} points.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  }, [tempPolygonPoints, toast]);

  const handlePolygonCancel = useCallback(() => {
    setIsCreatingPolygon(false);
    setTempPolygonPoints([]);
  }, []);

  // Form Field Handlers
  const handleFormFieldChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field-specific error
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const handleUnitsChange = useCallback((units) => {
    setFormData(prev => ({
      ...prev,
      units: Array.isArray(units) ? units : []
    }));
  }, []);



  // Effect to handle API errors
  useEffect(() => {
    if (createError) {
      toast({
        title: "Create Error",
        description: createError.message || "Failed to create clinical site.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [createError, toast]);

  useEffect(() => {
    if (updateError) {
      toast({
        title: "Update Error",
        description: updateError.message || "Failed to update clinical site.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [updateError, toast]);

  useEffect(() => {
    if (deleteError) {
      toast({
        title: "Delete Error",
        description: deleteError.message || "Failed to delete clinical site.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [deleteError, toast]);

  useEffect(() => {
    if (sitesError) {
      toast({
        title: "Data Load Error",
        description: sitesError.message || "Failed to load clinical sites.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [sitesError, toast]);

  return (
    <Box maxW="7xl" mx="auto" p={6} bg="gray.900" minH="100vh">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <HStack spacing={4}>
            <IconButton
              icon={<ArrowBackIcon />}
              variant="ghost"
              colorScheme="gray"
              onClick={() => navigate('/admin')}
              aria-label="Back to Admin Dashboard"
            />
            <VStack align="start" spacing={1}>
              <Heading size="lg" color="white">
                Clinical Sites Management
              </Heading>
              {selectedInstitution && (
                <Text fontSize="sm" color="gray.400">
                  {selectedInstitution} • {filteredSites.length} sites in {groupedSites.length} agencies
                </Text>
              )}
            </VStack>
          </HStack>
          
          {/* Institution Selector */}
          {availableInstitutions.length > 0 && (
            <Box minW="250px">
              <FormControl>
                <FormLabel fontSize="sm" color="gray.400" mb={1}>
                  {isSystemAdmin() ? "Select Institution" : "Institution"}
                </FormLabel>
                <Select
                  value={selectedInstitution}
                  onChange={(e) => setSelectedInstitution(e.target.value)}
                  bg="gray.700"
                  borderColor="gray.600"
                  color="white"
                  _focus={{ borderColor: "blue.400" }}
                  isDisabled={!isSystemAdmin() || availableInstitutions.length <= 1}
                >
                  {isSystemAdmin() && availableInstitutions.length > 1 && (
                    <option value="" style={{ backgroundColor: '#2D3748' }}>
                      All Institutions
                    </option>
                  )}
                  {availableInstitutions.map((inst) => (
                    <option key={inst.id} value={inst.institution} style={{ backgroundColor: '#2D3748' }}>
                      {inst.institution}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Flex>

        {/* Search and Filter Bar */}
        <Card bg="gray.800" color="white">
          <CardBody>
            <VStack spacing={4} align="stretch">


              <Flex
                direction={{ base: "column", md: "row" }}
                gap={4}
                align={{ base: "stretch", md: "center" }}
                justify="space-between"
              >
                <HStack spacing={4} flex={1}>
                  {/* Search Input */}
                  <InputGroup maxW={{ base: "full", md: "300px" }}>
                    <InputLeftElement>
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search sites..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      bg="gray.700"
                      borderColor="gray.600"
                      _placeholder={{ color: "gray.400" }}
                      _focus={{ borderColor: "blue.400" }}
                    />
                  </InputGroup>

                  {/* Facility Type Filter */}
                  <Select
                    placeholder="All Facility Types"
                    value={facilityTypeFilter}
                    onChange={(e) => setFacilityTypeFilter(e.target.value)}
                    maxW={{ base: "full", md: "200px" }}
                    bg="gray.700"
                    borderColor="gray.600"
                    _focus={{ borderColor: "blue.400" }}
                  >
                    {FACILITY_TYPES.map((type) => (
                      <option key={type.value} value={type.value} style={{ backgroundColor: '#2D3748' }}>
                        {type.label}
                      </option>
                    ))}
                  </Select>

                  {/* Active Status Filter */}
                  <Select
                    placeholder="All Statuses"
                    value={activeStatusFilter}
                    onChange={(e) => setActiveStatusFilter(e.target.value)}
                    maxW={{ base: "full", md: "150px" }}
                    bg="gray.700"
                    borderColor="gray.600"
                    _focus={{ borderColor: "blue.400" }}
                  >
                    <option value="true" style={{ backgroundColor: '#2D3748' }}>Active</option>
                    <option value="false" style={{ backgroundColor: '#2D3748' }}>Inactive</option>
                  </Select>
                </HStack>

                {/* Create Button and Counts */}
                <HStack spacing={4}>
                  <VStack spacing={1} align="end">
                    <Text fontSize="sm" color="gray.400">
                      {filteredSites.length} sites in {groupedSites.length} agencies
                    </Text>
                    {(searchTerm || facilityTypeFilter || activeStatusFilter) && (
                      <Text fontSize="xs" color="gray.500">
                        (filtered from {sites?.length || 0} total sites)
                      </Text>
                    )}
                  </VStack>
                  
                  {/* Expand/Collapse Controls */}
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="gray"
                      onClick={expandAllAgencies}
                      isDisabled={expandedAgencies.size === groupedSites.length}
                    >
                      Expand All
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="gray"
                      onClick={collapseAllAgencies}
                      isDisabled={expandedAgencies.size === 0}
                    >
                      Collapse All
                    </Button>
                  </HStack>

                  {hasSchedulingAccess() && (
                    <Button
                      leftIcon={<AddIcon />}
                      colorScheme="blue"
                      onClick={handleCreateSite}
                      size="md"
                    >
                      Create Site
                    </Button>
                  )}
                </HStack>
              </Flex>
            </VStack>
          </CardBody>
        </Card>

        {/* Sites Hierarchical View */}
        <Card bg="gray.800" color="white">
          <CardBody p={0}>
            {sitesFetching ? (
              <Center py={12}>
                <VStack spacing={4}>
                  <Spinner size="lg" color="blue.400" />
                  <Text color="gray.400">Loading clinical sites...</Text>
                </VStack>
              </Center>
            ) : sitesError ? (
              <Center py={12}>
                <VStack spacing={4}>
                  <Text color="red.400" fontSize="lg" fontWeight="semibold">
                    Failed to load clinical sites
                  </Text>
                  <Text color="gray.400" textAlign="center">
                    {sitesError.message || "An error occurred while loading the data."}
                  </Text>
                  <Button
                    colorScheme="blue"
                    onClick={() => refetchSites()}
                    size="sm"
                  >
                    Retry
                  </Button>
                </VStack>
              </Center>
            ) : groupedSites.length === 0 ? (
              <Center py={12}>
                <VStack spacing={4}>
                  <Text color="gray.400" fontSize="lg">
                    {sites?.length === 0 ? "No clinical sites found" : "No agencies or sites match your filters"}
                  </Text>
                  <Text color="gray.500" textAlign="center" fontSize="sm">
                    {sites?.length === 0 
                      ? "Create your first clinical site to get started."
                      : "Try adjusting your search criteria or filters."
                    }
                  </Text>
                  {sites?.length === 0 && hasSchedulingAccess() && (
                    <Button
                      colorScheme="blue"
                      onClick={handleCreateSite}
                      size="sm"
                      leftIcon={<AddIcon />}
                    >
                      Create First Site
                    </Button>
                  )}
                </VStack>
              </Center>
            ) : (
              <VStack spacing={0} align="stretch">
                {groupedSites.map(({ agency, sites: agencySites }) => {
                  const isExpanded = expandedAgencies.has(agency.id);
                  
                  return (
                    <Box key={agency.id}>
                      {/* Agency Header Row */}
                      <Box
                        bg="gray.750"
                        borderBottom="1px solid"
                        borderColor="gray.600"
                        _hover={{ bg: "gray.700" }}
                        cursor="pointer"
                        onClick={() => toggleAgencyExpansion(agency.id)}
                        role="group"
                      >
                        <Flex align="center" p={4} gap={4}>
                          {/* Expand/Collapse Icon */}
                          <IconButton
                            icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                            size="sm"
                            variant="ghost"
                            aria-label={isExpanded ? "Collapse agency" : "Expand agency"}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAgencyExpansion(agency.id);
                            }}
                          />
                
                          {/* Agency Name and Info */}
                          <Box flex={2} minW={0}>
                            <Flex align="center" gap={3} mb={1} flexWrap="wrap">
                              <Text fontSize="lg" fontWeight="bold" color="white" lineHeight="1.2">
                                {agency.name}
                              </Text>
                              <Badge
                                colorScheme={
                                  agency.facilityType === "hospital_system" ? "blue" :
                                  agency.facilityType === "fire_department" ? "red" :
                                  agency.facilityType === "ems_service" ? "orange" :
                                  agency.facilityType === "private_ambulance" ? "yellow" :
                                  "gray"
                                }
                                variant="solid"
                                fontSize="xs"
                              >
                                {FACILITY_TYPES.find(t => t.value === agency.facilityType)?.label || agency.facilityType}
                              </Badge>
                              {/* Inline Add Site Button */}
                              {hasSchedulingAccess() && (
                                <IconButton
                                  icon={<AddIcon />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="green"
                                  aria-label={`Add site to ${agency.name}`}
                                  title={`Add site to ${agency.name}`}
                                  opacity={0}
                                  _groupHover={{ opacity: 1 }}
                                  transition="opacity 0.2s ease"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCreateSiteForAgency(agency.id);
                                  }}
                                />
                              )}
                            </Flex>
                            <Text fontSize="sm" color="gray.400" lineHeight="1.2">
                              {agencySites.length} {agencySites.length === 1 ? 'clinical site' : 'clinical sites'}
                            </Text>
                          </Box>

                          {/* Affiliated Institutions */}
                          <Box flex={1} minW={0}>
                            <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1} lineHeight="1.2">
                              INSTITUTIONS
                            </Text>
                            {agency.institutionalAffiliations && Array.isArray(agency.institutionalAffiliations) && agency.institutionalAffiliations.length > 0 ? (
                              <Flex flexWrap="wrap" gap={1}>
                                {agency.institutionalAffiliations.map((institution, index) => (
                                  <Badge
                                    key={index}
                                    colorScheme={
                                      index % 4 === 0 ? "teal" :
                                      index % 4 === 1 ? "purple" :
                                      index % 4 === 2 ? "cyan" : "pink"
                                    }
                                    variant="subtle"
                                    fontSize="xs"
                                    maxW="120px"
                                    isTruncated
                                    title={institution}
                                  >
                                    {institution}
                                  </Badge>
                                ))}
                              </Flex>
                            ) : (
                              <Text fontSize="xs" color="gray.500" fontStyle="italic" lineHeight="1.2">
                                No affiliations
                              </Text>
                            )}
                          </Box>

                          {/* Agency Status */}
                          <Box>
                            <Badge
                              colorScheme={agency.isActive ? "green" : "red"}
                              variant="subtle"
                            >
                              {agency.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </Box>
                        </Flex>
                      </Box>

                      {/* Clinical Sites */}
                      {isExpanded && (
                        <Box bg="gray.800">
                          {agencySites.length === 0 ? (
                            <Box p={6} pl={16} borderBottom="1px solid" borderColor="gray.700">
                              <Text color="gray.500" fontSize="sm" fontStyle="italic">
                                No clinical sites found for this agency
                              </Text>
                            </Box>
                          ) : (
                            <>
                              {/* Header Row */}
                              <Box
                                borderBottom="1px solid"
                                borderColor="gray.600"
                                bg="gray.750"
                              >
                                <Flex align="center" p={3} pl={16} gap={4} fontSize="xs" fontWeight="semibold" color="gray.400">
                                  {/* Site Name */}
                                  <Box flex={1} minW={0} textAlign="left">
                                    SITE NAME
                                  </Box>
                                  {/* Institutions */}
                                  <Box w="140px" minW="140px" textAlign="center">
                                    INSTITUTIONS
                                  </Box>
                                  {/* Address */}
                                  <Box flex={1} minW={0} textAlign="center">
                                    ADDRESS
                                  </Box>
                                  {/* Contact Person */}
                                  <Box w="120px" minW="120px" textAlign="center">
                                    CONTACT
                                  </Box>
                                  {/* Phone */}
                                  <Box w="120px" minW="120px" textAlign="center">
                                    PHONE
                                  </Box>
                                  {/* Email */}
                                  <Box w="160px" minW="160px" textAlign="center">
                                    EMAIL
                                  </Box>
                                  {/* Status */}
                                  <Box w="80px" minW="80px" textAlign="center">
                                    STATUS
                                  </Box>
                                  {/* Geofence Status */}
                                  <Box w="100px" minW="100px" textAlign="center">
                                    GEOFENCE
                                  </Box>
                                  {/* Actions */}
                                  {hasSchedulingAccess() && (
                                    <Box w="100px" minW="100px" textAlign="center">
                                      ACTIONS
                                    </Box>
                                  )}
                                </Flex>
                              </Box>
                              {agencySites.map((site, index) => (
                                <Box
                                  key={site.id}
                                  borderBottom="1px solid"
                                  borderColor="gray.700"
                                  _hover={{ bg: "gray.750" }}
                                >
                                <Flex align="center" p={4} pl={16} gap={4}>
                                  {/* Site Name and Units */}
                                  <Box flex={1} minW={0}>
                                    <Flex align="center" gap={2} mb={1} flexWrap="wrap">
                                      <Text fontWeight="semibold" color="white" fontSize="md" lineHeight="1.2">
                                        {site.name}
                                      </Text>
                                      {site.needsGeofenceSetup && (
                                        <Badge colorScheme="orange" variant="solid" fontSize="xs">
                                          Needs Setup
                                        </Badge>
                                      )}
                                      {site.autoCreatedFromSync && !site.needsGeofenceSetup && (
                                        <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                          Auto-Created
                                        </Badge>
                                      )}
                                      {site.units && Array.isArray(site.units) && site.units.length > 0 && (
                                        <>
                                          {site.units.map((unit, unitIndex) => (
                                            <Badge key={unitIndex} size="sm" colorScheme="purple" variant="subtle">
                                              {unit}
                                            </Badge>
                                          ))}
                                        </>
                                      )}
                                    </Flex>
                                  </Box>

                                  {/* Institutions (from parent agency) */}
                                  <Box w="140px" minW="140px" display="flex" alignItems="center" justifyContent="center" textAlign="center">
                                    {site.agency?.institutionalAffiliations && Array.isArray(site.agency.institutionalAffiliations) && site.agency.institutionalAffiliations.length > 0 ? (
                                      <Box>
                                        <Flex direction="column" gap={1} align="center">
                                          {site.agency.institutionalAffiliations.slice(0, 2).map((institution, index) => (
                                            <Badge
                                              key={index}
                                              colorScheme={
                                                index % 4 === 0 ? "teal" :
                                                index % 4 === 1 ? "purple" :
                                                index % 4 === 2 ? "cyan" : "pink"
                                              }
                                              variant="subtle"
                                              fontSize="xs"
                                              maxW="130px"
                                              isTruncated
                                              title={institution}
                                            >
                                              {institution}
                                            </Badge>
                                          ))}
                                          {site.agency.institutionalAffiliations.length > 2 && (
                                            <Text fontSize="xs" color="gray.500">
                                              +{site.agency.institutionalAffiliations.length - 2} more
                                            </Text>
                                          )}
                                        </Flex>
                                      </Box>
                                    ) : (
                                      <Text fontSize="xs" color="gray.500" fontStyle="italic">
                                        None
                                      </Text>
                                    )}
                                  </Box>

                                  {/* Address */}
                                  <Box flex={1} minW={0} display="flex" alignItems="center" justifyContent="center" textAlign="center">
                                    <Text color="gray.300" fontSize="sm" noOfLines={2} lineHeight="1.3">
                                      {site.address}
                                    </Text>
                                  </Box>

                                  {/* Contact Person */}
                                  <Box w="120px" minW="120px" display="flex" alignItems="center" justifyContent="center" textAlign="center">
                                    <Text color="gray.300" fontSize="sm" noOfLines={1} lineHeight="1.3">
                                      {site.siteContactPerson}
                                    </Text>
                                  </Box>

                                  {/* Phone */}
                                  <Box w="120px" minW="120px" display="flex" alignItems="center" justifyContent="center" textAlign="center">
                                    <Text color="gray.300" fontSize="sm" noOfLines={1} lineHeight="1.3">
                                      {site.sitePhone}
                                    </Text>
                                  </Box>

                                  {/* Email */}
                                  <Box w="160px" minW="160px" display="flex" alignItems="center" justifyContent="center" textAlign="center">
                                    <Text color="gray.300" fontSize="sm" noOfLines={1} lineHeight="1.3">
                                      {site.siteEmail}
                                    </Text>
                                  </Box>

                                  {/* Status */}
                                  <Box w="80px" minW="80px" display="flex" justifyContent="center">
                                    <Badge
                                      colorScheme={site.isActive ? "green" : "red"}
                                      variant="subtle"
                                      fontSize="xs"
                                    >
                                      {site.isActive ? "Active" : "Inactive"}
                                    </Badge>
                                  </Box>

                                  {/* Geofence Status */}
                                  <Box w="100px" minW="100px" display="flex" justifyContent="center">
                                    <Badge
                                      colorScheme={
                                        site.geofenceType === "none" ? "gray" :
                                        site.geofenceType === "circular" ? "blue" :
                                        site.geofenceType === "polygon" ? "purple" : "gray"
                                      }
                                      variant="subtle"
                                      fontSize="xs"
                                    >
                                      {site.geofenceType === "none" ? "No Fence" :
                                       site.geofenceType === "circular" ? "Circle" :
                                       site.geofenceType === "polygon" ? "Polygon" : "Unknown"}
                                    </Badge>
                                  </Box>

                                  {/* Actions */}
                                  {hasSchedulingAccess() && (
                                    <Box w="100px" minW="100px" display="flex" justifyContent="center">
                                      <HStack spacing={2}>
                                        <IconButton
                                          icon={<EditIcon />}
                                          size="sm"
                                          variant="ghost"
                                          colorScheme="blue"
                                          onClick={() => handleEditSite(site)}
                                          aria-label="Edit site"
                                        />
                                        <IconButton
                                          icon={<DeleteIcon />}
                                          size="sm"
                                          variant="ghost"
                                          colorScheme="red"
                                          onClick={() => handleDeleteSite(site)}
                                          aria-label="Delete site"
                                        />
                                      </HStack>
                                    </Box>
                                  )}
                                </Flex>
                                </Box>
                              ))}
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </VStack>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Create Site Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={handleCloseModals} size="4xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent bg="gray.800" color="white" maxH="90vh">
          <ModalHeader borderBottomWidth="1px" borderColor="gray.700">
            <Heading size="lg">Create New Clinical Site</Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={6} align="stretch">
              {/* Basic Information Section */}
              <Box>
                <Heading size="md" mb={4} color="blue.400">
                  Basic Information
                </Heading>
                <Grid templateColumns={{ base: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" }} gap={4}>
                  <FormControl isRequired isInvalid={formErrors.name}>
                    <FormLabel>Site Name</FormLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleFormFieldChange('name', e.target.value)}
                      placeholder="Enter site name"
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    />
                    <FormErrorMessage>{formErrors.name}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={formErrors.agencyId}>
                    <FormLabel>Agency</FormLabel>
                    <Select
                      value={formData.agencyId}
                      onChange={(e) => handleFormFieldChange('agencyId', e.target.value)}
                      placeholder="Select agency"
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    >
                      {agencies?.map((agency) => (
                        <option key={agency.id} value={agency.id} style={{ backgroundColor: '#2D3748' }}>
                          {agency.name} ({FACILITY_TYPES.find(t => t.value === agency.facilityType)?.label || agency.facilityType})
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{formErrors.agencyId}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={formErrors.siteContactPerson}>
                    <FormLabel>Contact Person</FormLabel>
                    <Input
                      value={formData.siteContactPerson}
                      onChange={(e) => handleFormFieldChange('siteContactPerson', e.target.value)}
                      placeholder="Enter contact person name"
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    />
                    <FormErrorMessage>{formErrors.siteContactPerson}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={formErrors.sitePhone}>
                    <FormLabel>Phone Number</FormLabel>
                    <Input
                      value={formData.sitePhone}
                      onChange={(e) => handleFormFieldChange('sitePhone', e.target.value)}
                      placeholder="Enter phone number (optional)"
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    />
                    <FormErrorMessage>{formErrors.sitePhone}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={formErrors.siteEmail}>
                    <FormLabel>Email Address</FormLabel>
                    <Input
                      type="email"
                      value={formData.siteEmail}
                      onChange={(e) => handleFormFieldChange('siteEmail', e.target.value)}
                      placeholder="Enter email address (optional)"
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    />
                    <FormErrorMessage>{formErrors.siteEmail}</FormErrorMessage>
                  </FormControl>
                </Grid>
              </Box>

              {/* Shift Types Section */}
              <Box>
                <Heading size="md" mb={4} color="blue.400">
                  Shift Types
                </Heading>
                <FormControl>
                  <FormLabel>Select shift types available at this site</FormLabel>
                  <CheckboxGroup
                    value={formData.units}
                    onChange={handleUnitsChange}
                  >
                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={2}>
                      {SHIFT_TYPES.map((unit) => (
                        <Checkbox key={unit.value} value={unit.value} color="white">
                          {unit.label}
                        </Checkbox>
                      ))}
                    </Grid>
                  </CheckboxGroup>
                </FormControl>
              </Box>

              {/* Location Section */}
              <Box>
                <Heading size="md" mb={4} color="blue.400">
                  Location
                </Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl isInvalid={formErrors.coordinates}>
                    <FormLabel>Address</FormLabel>
                    <AddressSearch
                      onAddressSelect={handleAddressSelect}
                      placeholder="Search for site address..."
                    />
                    <FormErrorMessage>{formErrors.coordinates}</FormErrorMessage>
                  </FormControl>

                  {formData.address && (
                    <Box>
                      <Text fontSize="sm" color="gray.400" mb={2}>Selected Address:</Text>
                      <Text fontSize="sm" color="gray.300" p={3} bg="gray.700" borderRadius="md">
                        {formData.address}
                      </Text>
                    </Box>
                  )}

                  <Box>
                    <Text fontSize="sm" color="gray.400" mb={2}>Map Preview:</Text>
                    <MapPicker
                      latitude={formData.latitude}
                      longitude={formData.longitude}
                      address={formData.address}
                      onCoordinatesChange={handleCoordinatesChange}
                      geofenceType={formData.geofenceType}
                      geofenceRadius={formData.geofenceRadius}
                      geofencePolygon={formData.geofencePolygon}
                      isCreatingPolygon={isCreatingPolygon}
                      tempPolygonPoints={tempPolygonPoints}
                      onPolygonPointAdd={handlePolygonPointAdd}
                      onPolygonComplete={handlePolygonComplete}
                      onPolygonCancel={handlePolygonCancel}
                    />
                  </Box>
                </VStack>
              </Box>



              {/* Geofencing Section */}
              <Box>
                <Heading size="md" mb={4} color="blue.400">
                  Geofencing
                </Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Geofence Type</FormLabel>
                    <Select
                      value={formData.geofenceType}
                      onChange={(e) => handleGeofenceTypeChange(e.target.value)}
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    >
                      {GEOFENCE_TYPES.map((type) => (
                        <option key={type.value} value={type.value} style={{ backgroundColor: '#2D3748' }}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {formData.geofenceType === "circular" && (
                    <FormControl isInvalid={formErrors.geofenceRadius}>
                      <FormLabel>Radius (meters)</FormLabel>
                      <NumberInput
                        value={formData.geofenceRadius}
                        onChange={(valueString) => handleGeofenceRadiusChange(valueString)}
                        min={10}
                        max={10000}
                        bg="gray.700"
                      >
                        <NumberInputField
                          bg="gray.700"
                          borderColor="gray.600"
                          _focus={{ borderColor: "blue.400" }}
                        />
                      </NumberInput>
                      <FormErrorMessage>{formErrors.geofenceRadius}</FormErrorMessage>
                    </FormControl>
                  )}

                  {formData.geofenceType === "polygon" && (
                    <FormControl isInvalid={formErrors.geofencePolygon}>
                      <FormLabel>Polygon Area</FormLabel>
                      <VStack spacing={2} align="stretch">
                        <Text fontSize="sm" color="gray.400">
                          Click on the map above to create polygon points. Need at least 3 points.
                        </Text>
                        {formData.geofencePolygon.length > 0 && (
                          <Text fontSize="sm" color="green.400">
                            Current polygon has {formData.geofencePolygon.length} points
                          </Text>
                        )}
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() => setIsCreatingPolygon(true)}
                            isDisabled={isCreatingPolygon}
                          >
                            {formData.geofencePolygon.length > 0 ? 'Edit Polygon' : 'Create Polygon'}
                          </Button>
                          {formData.geofencePolygon.length > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="red"
                              onClick={() => handleFormFieldChange('geofencePolygon', [])}
                            >
                              Clear Polygon
                            </Button>
                          )}
                        </HStack>
                      </VStack>
                      <FormErrorMessage>{formErrors.geofencePolygon}</FormErrorMessage>
                    </FormControl>
                  )}
                </VStack>
              </Box>



              {/* Notes and Status Section */}
              <Box>
                <Heading size="md" mb={4} color="blue.400">
                  Additional Information
                </Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Notes</FormLabel>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => handleFormFieldChange('notes', e.target.value)}
                      placeholder="Additional notes about this clinical site..."
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                      rows={4}
                    />
                  </FormControl>

                  <FormControl>
                    <Checkbox
                      isChecked={formData.isActive}
                      onChange={(e) => handleFormFieldChange('isActive', e.target.checked)}
                      color="white"
                    >
                      Site is active and available for assignments
                    </Checkbox>
                  </FormControl>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" borderColor="gray.700">
            <HStack spacing={3}>
              <Button variant="ghost" onClick={handleCloseModals}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSubmitCreate}
                isLoading={createFetching}
                loadingText="Creating..."
              >
                Create Site
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Site Modal */}
      <Modal isOpen={isEditModalOpen} onClose={handleCloseModals} size="4xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent bg="gray.800" color="white" maxH="90vh">
          <ModalHeader borderBottomWidth="1px" borderColor="gray.700">
            <Heading size="lg">Edit Clinical Site</Heading>
            {editingSite && (
              <Text fontSize="sm" color="gray.400" mt={1}>
                {editingSite.name}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={6} align="stretch">
              {/* Basic Information Section */}
              <Box>
                <Heading size="md" mb={4} color="blue.400">
                  Basic Information
                </Heading>
                <Grid templateColumns={{ base: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" }} gap={4}>
                  <FormControl isRequired isInvalid={formErrors.name}>
                    <FormLabel>Site Name</FormLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleFormFieldChange('name', e.target.value)}
                      placeholder="Enter site name"
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    />
                    <FormErrorMessage>{formErrors.name}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={formErrors.agencyId}>
                    <FormLabel>Agency</FormLabel>
                    <Select
                      value={formData.agencyId}
                      onChange={(e) => handleFormFieldChange('agencyId', e.target.value)}
                      placeholder="Select agency"
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    >
                      {agencies?.map((agency) => (
                        <option key={agency.id} value={agency.id} style={{ backgroundColor: '#2D3748' }}>
                          {agency.name} ({FACILITY_TYPES.find(t => t.value === agency.facilityType)?.label || agency.facilityType})
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{formErrors.agencyId}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={formErrors.siteContactPerson}>
                    <FormLabel>Contact Person</FormLabel>
                    <Input
                      value={formData.siteContactPerson}
                      onChange={(e) => handleFormFieldChange('siteContactPerson', e.target.value)}
                      placeholder="Enter contact person name"
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    />
                    <FormErrorMessage>{formErrors.siteContactPerson}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={formErrors.sitePhone}>
                    <FormLabel>Phone Number</FormLabel>
                    <Input
                      value={formData.sitePhone}
                      onChange={(e) => handleFormFieldChange('sitePhone', e.target.value)}
                      placeholder="Enter phone number (optional)"
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    />
                    <FormErrorMessage>{formErrors.sitePhone}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={formErrors.siteEmail}>
                    <FormLabel>Email Address</FormLabel>
                    <Input
                      type="email"
                      value={formData.siteEmail}
                      onChange={(e) => handleFormFieldChange('siteEmail', e.target.value)}
                      placeholder="Enter email address (optional)"
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    />
                    <FormErrorMessage>{formErrors.siteEmail}</FormErrorMessage>
                  </FormControl>
                </Grid>
              </Box>

              {/* Shift Types Section */}
              <Box>
                <Heading size="md" mb={4} color="blue.400">
                  Shift Types
                </Heading>
                <FormControl>
                  <FormLabel>Select shift types available at this site</FormLabel>
                  <CheckboxGroup
                    value={formData.units}
                    onChange={handleUnitsChange}
                  >
                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={2}>
                      {SHIFT_TYPES.map((unit) => (
                        <Checkbox key={unit.value} value={unit.value} color="white">
                          {unit.label}
                        </Checkbox>
                      ))}
                    </Grid>
                  </CheckboxGroup>
                </FormControl>
              </Box>

              {/* Location Section */}
              <Box>
                <Heading size="md" mb={4} color="blue.400">
                  Location
                </Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl isInvalid={formErrors.coordinates}>
                    <FormLabel>Address</FormLabel>
                    <AddressSearch
                      onAddressSelect={handleAddressSelect}
                      placeholder="Search for site address..."
                    />
                    <FormErrorMessage>{formErrors.coordinates}</FormErrorMessage>
                  </FormControl>

                  {formData.address && (
                    <Box>
                      <Text fontSize="sm" color="gray.400" mb={2}>Selected Address:</Text>
                      <Text fontSize="sm" color="gray.300" p={3} bg="gray.700" borderRadius="md">
                        {formData.address}
                      </Text>
                    </Box>
                  )}

                  <Box>
                    <Text fontSize="sm" color="gray.400" mb={2}>Map Preview:</Text>
                    <MapPicker
                      latitude={formData.latitude}
                      longitude={formData.longitude}
                      address={formData.address}
                      onCoordinatesChange={handleCoordinatesChange}
                      geofenceType={formData.geofenceType}
                      geofenceRadius={formData.geofenceRadius}
                      geofencePolygon={formData.geofencePolygon}
                      isCreatingPolygon={isCreatingPolygon}
                      tempPolygonPoints={tempPolygonPoints}
                      onPolygonPointAdd={handlePolygonPointAdd}
                      onPolygonComplete={handlePolygonComplete}
                      onPolygonCancel={handlePolygonCancel}
                    />
                  </Box>
                </VStack>
              </Box>

              {/* Geofencing Section */}
              <Box>
                <Heading size="md" mb={4} color="blue.400">
                  Geofencing
                </Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Geofence Type</FormLabel>
                    <Select
                      value={formData.geofenceType}
                      onChange={(e) => handleGeofenceTypeChange(e.target.value)}
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                    >
                      {GEOFENCE_TYPES.map((type) => (
                        <option key={type.value} value={type.value} style={{ backgroundColor: '#2D3748' }}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {formData.geofenceType === "circular" && (
                    <FormControl isInvalid={formErrors.geofenceRadius}>
                      <FormLabel>Radius (meters)</FormLabel>
                      <NumberInput
                        value={formData.geofenceRadius}
                        onChange={(valueString) => handleGeofenceRadiusChange(valueString)}
                        min={10}
                        max={10000}
                        bg="gray.700"
                      >
                        <NumberInputField
                          bg="gray.700"
                          borderColor="gray.600"
                          _focus={{ borderColor: "blue.400" }}
                        />
                      </NumberInput>
                      <FormErrorMessage>{formErrors.geofenceRadius}</FormErrorMessage>
                    </FormControl>
                  )}

                  {formData.geofenceType === "polygon" && (
                    <FormControl isInvalid={formErrors.geofencePolygon}>
                      <FormLabel>Polygon Area</FormLabel>
                      <VStack spacing={2} align="stretch">
                        <Text fontSize="sm" color="gray.400">
                          Click on the map above to create polygon points. Need at least 3 points.
                        </Text>
                        {formData.geofencePolygon.length > 0 && (
                          <Text fontSize="sm" color="green.400">
                            Current polygon has {formData.geofencePolygon.length} points
                          </Text>
                        )}
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() => setIsCreatingPolygon(true)}
                            isDisabled={isCreatingPolygon}
                          >
                            {formData.geofencePolygon.length > 0 ? 'Edit Polygon' : 'Create Polygon'}
                          </Button>
                          {formData.geofencePolygon.length > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="red"
                              onClick={() => handleFormFieldChange('geofencePolygon', [])}
                            >
                              Clear Polygon
                            </Button>
                          )}
                        </HStack>
                      </VStack>
                      <FormErrorMessage>{formErrors.geofencePolygon}</FormErrorMessage>
                    </FormControl>
                  )}
                </VStack>
              </Box>

              {/* Notes and Status Section */}
              <Box>
                <Heading size="md" mb={4} color="blue.400">
                  Additional Information
                </Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Notes</FormLabel>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => handleFormFieldChange('notes', e.target.value)}
                      placeholder="Additional notes about this clinical site..."
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{ borderColor: "blue.400" }}
                      rows={4}
                    />
                  </FormControl>

                  <FormControl>
                    <Checkbox
                      isChecked={formData.isActive}
                      onChange={(e) => handleFormFieldChange('isActive', e.target.checked)}
                      color="white"
                    >
                      Site is active and available for assignments
                    </Checkbox>
                  </FormControl>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" borderColor="gray.700">
            <HStack spacing={3}>
              <Button variant="ghost" onClick={handleCloseModals}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSubmitUpdate}
                isLoading={updateFetching}
                loadingText="Updating..."
              >
                Update Site
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <AlertDialog
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModals}
        leastDestructiveRef={useRef()}
      >
        <AlertDialogOverlay bg="blackAlpha.600">
          <AlertDialogContent bg="gray.800" color="white">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" borderBottomWidth="1px" borderColor="gray.700">
              Delete Clinical Site
            </AlertDialogHeader>

            <AlertDialogBody py={6}>
              <VStack spacing={4} align="stretch">
                <Text>
                  Are you sure you want to delete this clinical site? This action cannot be undone.
                </Text>
                
                {deletingSite && (
                  <Box p={4} bg="gray.700" borderRadius="md" borderLeft="4px solid" borderColor="red.400">
                    <VStack align="start" spacing={2}>
                      <HStack>
                        <Text fontWeight="semibold">Site:</Text>
                        <Text>{deletingSite.name}</Text>
                      </HStack>
                      <HStack>
                        <Text fontWeight="semibold">Address:</Text>
                        <Text fontSize="sm" color="gray.300">{deletingSite.address}</Text>
                      </HStack>
                      <HStack>
                        <Text fontWeight="semibold">Agency:</Text>
                        <Text fontSize="sm" color="gray.300">{deletingSite.agency?.name}</Text>
                      </HStack>
                    </VStack>
                  </Box>
                )}

                <Box p={3} bg="red.900" borderRadius="md" border="1px solid" borderColor="red.600">
                  <Text fontSize="sm" color="red.200">
                    <strong>Warning:</strong> Deleting this clinical site will permanently remove all associated data, 
                    including any shift assignments, student assignments, and historical records. This action cannot be reversed.
                  </Text>
                </Box>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter borderTopWidth="1px" borderColor="gray.700">
              <HStack spacing={3}>
                <Button 
                  variant="ghost" 
                  onClick={handleCloseModals}
                  isDisabled={deleteFetching}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleConfirmDelete}
                  isLoading={deleteFetching}
                  loadingText="Deleting..."
                >
                  Delete Site
                </Button>
              </HStack>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
